// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Clickable, Icon, Typography } from "@insite/mobius/index";
import setFixedQuickOrderIsOpen from "@rc/App/Handlers/FixedQuickOrder/SetFixedQuickOrderIsOpen";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { fixedQuickOrderStyles } from "@rc/App/WidgetExtensions/FixedQuickOrder/FixedQuickOrderExtentions";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const settingsCollection = getSettingsCollection(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    return {
        isAuthenticated,
        allowQuickOrder: settingsCollection?.orderSettings?.allowQuickOrder,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        excludeQuickOrderFromNavigation: state.links.pageLinks?.[0]?.children?.filter(
            page => page.type === "QuickOrderPage",
        )?.[0]?.excludeFromNavigation,
        isFixedQuickOrderOpened: state.xFixedQuickOrderState?.isOpen,
    };
};

const mapDispatchToProps = {
    setFixedQuickOrderIsOpen,
};

export type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = fixedQuickOrderStyles;

const FixedQuickOrder = ({
    id,
    isAuthenticated,
    allowQuickOrder,
    navDrawerIsOpen,
    excludeQuickOrderFromNavigation,
    isFixedQuickOrderOpened,
    setFixedQuickOrderIsOpen,
}: Props) => {
    if (!isAuthenticated || excludeQuickOrderFromNavigation) {
        return null;
    }

    return (
        <StyledWrapper id="fixed-quick-order" {...styles.container}>
            <Clickable
                {...styles.trigger}
                {...{ isFixedQuickOrderOpened, navDrawerIsOpen }}
                onClick={() => {
                    setFixedQuickOrderIsOpen({ isOpen: !isFixedQuickOrderOpened });
                }}
            >
                <Typography {...styles.triggerLabel}>{translate("Quick Order")}</Typography>
                <Icon {...styles.triggerIcon}></Icon>
                {isFixedQuickOrderOpened && <Icon {...styles.closeIcon}></Icon>}
            </Clickable>
            {allowQuickOrder && (
                <StyledWrapper {...styles.panel} {...{ isFixedQuickOrderOpened }}>
                    {createWidgetElement("CompactQuickOrder/CompactQuickOrder", {
                        fields: {
                            isFixedQuickOrder: true,
                        },
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FixedQuickOrder),
    definition: {
        displayName: "Fixed Quick Order - Left Side Of The Page",
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [],
    },
};

export default widgetModule;
